<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
      </div>
    </div>
    <Alert v-if="apiStatus.fail.calculatePremium || apiStatus.fail.refData" alertType="error" />
    <div class="calculator-and-info-container">
      <section class="content fr-view">
        <ApiLoading v-if="apiStatus.loading.refData" :apiError="apiStatus.fail.refData"></ApiLoading>
        <div v-if="content !== null && content.topContent !== undefined && !apiStatus.loading.refData && !apiStatus.fail.refData" v-html="content.topContent" />
      </section>
      <form v-if="!apiStatus.loading.refData" class="calculator-wrapper" @submit.prevent novalidate>
        <div class="calculator">
          <h2 :class="[paidToDate ? 'h3-DPT' : 'h3-no-DPT']">Calculator</h2>
          <p v-if="paidToDate" class="p">
            The membership is covered until
            <strong>{{ formatDateToDisplay(reverseFormatFormDate(paidToDate)) }}.</strong>
          </p>
          <label class="calculate-premium-by-header">Calculate premium by</label>
          <ul class="radio-group large">
            <li v-for="option in calculateByOptions" :key="`calculate by ${option.display}`">
              <input type="radio" name="calculateBy" :id="`calculate-by-${option.value}`" v-model="calculateBy" :value="option.value" />
              <label :for="`calculate-by-${option.value}`">
                {{ option.display }}
              </label>
            </li>
          </ul>

          <label class="date-amount-input-label" for="premium-calculate-input">{{ isByDate ? 'The last full date of cover required is' : 'Payment amount' }}</label>
          <FormInput
            v-if="isByDate"
            type="text"
            v-model="v$.inputValue.$model"
            id="premium-calculate-input"
            :overrideClass="['input-field', 'date-input', v$.inputValue.$error ? 'error' : ''].join(' ')"
            :readOnly="showResult"
            placeholder="DD / MM / YYYY"
            v-maska="'## / ## / ####'"
            maxlength="14"
            isDateInput
          />
          <div v-else class="wrapper-with-prefix" :class="{ 'mock-input-focus': mockInputStatusfocus && !showResult, error: v$.inputValue.$invalid }">
            <div class="prefixed-input">
              <label for="premium-calculate-input" class="prefix">$</label>
            </div>
            <input
              class="prefixed-style"
              type="number"
              v-model="v$.inputValue.$model"
              id="premium-calculate-input"
              :readonly="showResult"
              placeholder="0.00"
              @focus="mockInputFocus(true)"
              @blur="mockInputFocus(false)"
              @keydown="preventInvalidCurrencyInput"
            />
          </div>
          <label v-if="v$.inputValue.$error" class="validation-error-msg" for="premium-calculate-input">{{ validationErrorMessage }}</label>
          <Alert
            v-if="!isByDate && returnedDateExceed12Months"
            message="Premiums can be paid up to 12 months in advance. This amount exceeds 12 months of cover. Please enter another amount."
            class="inline-message"
            alertType="error"
          />

          <ApiLoading v-if="apiStatus.loading.calculatePremium || apiStatus.fail.calculatePremium" :apiError="apiStatus.fail.calculatePremium" class="loading-premium"></ApiLoading>

          <div v-if="showResult && !apiStatus.fail.calculatePremium" class="result">
            <p>
              {{ isByDate ? 'Payment amount' : 'The last full day of cover after this payment is' }}
            </p>
            <span>
              {{ result }}
            </span>
          </div>

          <div v-if="!apiStatus.loading.calculatePremium" class="radio-group large button-wrapper">
            <template v-if="showResult && !apiStatus.fail.calculatePremium">
              <input type="reset" class="secondary" @click="reset" value="Reset" />
              <input type="submit" @click="payNow" value="Pay now" />
            </template>
            <!-- <button v-else id="trigger-reset" @click="calculatePremium" :disabled="v$.$invalid || (!isByDate && returnedDateExceed12Months)">Reset</button> -->
            <button v-else id="trigger-calculate" @click="calculatePremium" :disabled="v$.$invalid || (!isByDate && returnedDateExceed12Months)">Calculate</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { AccessLevel } from '@/enums';
import { MY_COVER_MAKE_A_PAYMENT, ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { PremiumCalculator as Services } from '@/services/MyCover';
import { ApiLoading, Alert, FormInput } from '@/components';
import {
  formatDateToDisplay,
  convertDateToDDMMYYYYWithSlash,
  formatAsCurrency,
  isEmptyString,
  preventInvalidCurrencyInput,
  isValidCurrency,
  isAcceptableAmount,
  getAccessLevel,
} from '@/utils';
import { DATE_VALIDATOR } from '@general/js/validators';
import { isPastDate, isDateBefore, reverseFormatFormDate, getDurationInterval, getCurrentDate, formatFormDate, formatDate } from '@general/js/utils';
import ErrorStrings from '@general/js/constants/ErrorStrings';

const CALCULATE_BY_OPTIONS = [
  { display: 'Date of cover', value: 'date', icon: 'calendar' },
  { display: 'Payment amount', value: 'amount', icon: 'dollar-sign' },
];
const INITIAL_SELECTED_OPTION = CALCULATE_BY_OPTIONS[0].value; // initial selected value is `date` as requirement
const ERROR_CALCULATED_DATE_EXCEED_12_MONTHS = 'returned date exceed 12 months from today';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'PremiumCalculator',

  components: {
    ApiLoading,
    Alert,
    FormInput,
  },

  data() {
    return {
      calculateByOptions: CALCULATE_BY_OPTIONS,
      calculateBy: INITIAL_SELECTED_OPTION,
      inputValue: '',
      showResult: false,
      result: '',
      apiStatus: {
        loading: {
          refData: true,
          calculatePremium: false,
        },
        fail: {
          refData: false,
          calculatePremium: false,
        },
      },
      content: null,
      mockInputStatusfocus: false,
      paidToDate: undefined, // expect to be yyyy-mm-dd format
      coverStartDate: undefined, // expect to be yyyy-mm-dd format
      returnedDateExceed12Months: false,
    };
  },

  computed: {
    ...mapGetters({
      currentPolicyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentPersonCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      memberships: VUEX_GENERAL.GETTERS.memberships,
    }),

    isByDate() {
      return this.calculateBy === INITIAL_SELECTED_OPTION;
    },

    isPaidToDateAvailable() {
      return !this.apiStatus.fail.refData && !this.apiStatus.loading.refData && !isEmptyString(this.paidToDate);
    },

    validationErrorMessage() {
      const vObj = this.v$.inputValue;
      let msg = '';

      if (this.isByDate) {
        // Validation error message for date input

        // entered date is more than 12 months from today
        if (vObj.notMoreThan12Months.$invalid) {
          msg = 'Premiums can be paid up to 12 months in advance. This date exceeds 12 months. Please enter another date.';
        }

        // only if paidToDate is available and entered date is not future date compare to paid to date
        if (vObj.notBeforePaidToDate.$invalid) {
          msg = 'You are already covered up to and including this date. Please enter another date. Premiums can be paid up to 12 months in advance.';
        }

        // date is in past
        if (vObj.notPastDate.$invalid) {
          msg = 'Premiums must be paid in advance. Please enter a future date.';
        }

        // incorrect date format
        if (vObj.DATE_VALIDATOR.$invalid) {
          msg = "We can't accept that - please remove letters and spaces.";
        }
      } else {
        // Validation error message for payment amount input

        // payment amount input must be min $1.00 and max $20,500
        if (vObj.isAcceptableAmount.$invalid) {
          msg = ErrorStrings.CURRENCY_INPUT.NOT_ACCEPTABLE_AMOUNT();
        }

        // invalid currency input
        if (vObj.isValidCurrency.$invalid) {
          msg = ErrorStrings.CURRENCY_INPUT.NOT_VALID_CURRENCY;
        }
      }

      return msg;
    },
  },

  watch: {
    inputValue() {
      if (this.returnedDateExceed12Months) {
        this.returnedDateExceed12Months = false;
      }
    },

    calculateBy() {
      this.reset();
    },
  },

  mounted() {
    const accessLevel = getAccessLevel(this.currentPolicyNumber, this.memberships);
    const isSelfAccess = accessLevel === AccessLevel.SELF_ACCESS;

    if (isSelfAccess) {
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }

    this.getReferenceData();
  },

  validations() {
    const validationObj = {
      inputValue: { required },
    };

    if (this.isByDate) {
      validationObj.inputValue = {
        ...validationObj.inputValue,
        DATE_VALIDATOR,
        notPastDate(date) {
          return !isPastDate(date);
        },
        notMoreThan12Months(date) {
          return this.isWithin12MonthsFromToday(date);
        },
        notBeforePaidToDate(date) {
          return isDateBefore(date, reverseFormatFormDate(this.isPaidToDateAvailable ? this.paidToDate : this.coverStartDate));
        },
      };
    } else {
      validationObj.inputValue = {
        ...validationObj.inputValue,
        isValidCurrency,
        isAcceptableAmount(amount) {
          return isAcceptableAmount(amount);
        },
      };
    }

    return validationObj;
  },

  methods: {
    /**
     * Check if given date is within 12 months (1 year) or not. excluding today
     * @param {string} date - date in `dd / mm / yyyy`
     * @returns {boolean} - true if is within 12 months
     */
    isWithin12MonthsFromToday(date) {
      const todayPlusOneDay = new Date(new Date(getCurrentDate()).getTime() + 24 * 60 * 60 * 1000);
      const duration = getDurationInterval(todayPlusOneDay, new Date(formatFormDate(date)));
      return duration?.years === 0;
    },

    mockInputFocus(bool) {
      this.mockInputStatusfocus = bool;
    },

    reset() {
      this.returnedDateExceed12Months = false;
      this.showResult = false;
      this.result = '';
      this.inputValue = '';
      this.apiStatus.fail.calculatePremium = false;
      this.v$.$reset();
    },

    async getReferenceData() {
      this.apiStatus.fail.refData = false;
      this.apiStatus.loading.refData = true;

      try {
        const res = await Services.getPremiumCalculatorReferenceData(this.currentPolicyNumber);
        this.content = {
          topContent: res.referenceData.premiumCalculatorInformationText.informationText,
        };
        this.coverStartDate = res?.coverStartDate;

        if (res?.datePaidTo) {
          this.paidToDate = res.datePaidTo;
        }
      } catch (err) {
        this.apiStatus.fail.refData = true;
        logger.error(err);
      } finally {
        this.apiStatus.loading.refData = false;
      }
    },

    async calculatePremium() {
      this.result = '';
      this.returnedDateExceed12Months = false;
      this.showResult = false;
      this.mockInputFocus(false);
      this.apiStatus.fail.calculatePremium = false;
      this.apiStatus.loading.calculatePremium = true;

      try {
        let startDate = getCurrentDate();
        if (this.paidToDate !== undefined) {
          startDate = this.paidToDate;
          startDate = new Date(new Date(startDate).getTime() + 24 * 60 * 60 * 1000); // Add +1 day to start date
        } else if (this.coverStartDate !== undefined) {
          startDate = this.coverStartDate;
        }
        const res = await Services.calculatePremium(this.calculateBy, this.currentPolicyNumber, formatDate(startDate), this.inputValue);

        if (!this.isByDate && !this.isWithin12MonthsFromToday(reverseFormatFormDate(res))) {
          /**
           * Technically, when returned calculated date exceed 12 months from today, it is not api fail.
           * However, as requirement, we are treating it as invalid return and show error message.
           */
          throw ERROR_CALCULATED_DATE_EXCEED_12_MONTHS;
        }

        this.result = !this.isByDate ? formatDateToDisplay(convertDateToDDMMYYYYWithSlash(res)) : formatAsCurrency(res);
        this.showResult = true;
      } catch (err) {
        switch (err) {
          case ERROR_CALCULATED_DATE_EXCEED_12_MONTHS:
            this.returnedDateExceed12Months = true;
            break;

          default:
            this.apiStatus.fail.calculatePremium = true;
            break;
        }

        logger.error(err);
      } finally {
        this.apiStatus.loading.calculatePremium = false;
      }
    },

    payNow() {
      setNavigationState({ paymentAmount: CALCULATE_BY_OPTIONS[0].value === this.calculateBy ? this.result.replace('$', '') : this.inputValue });
      this.$router.push({ name: MY_COVER_MAKE_A_PAYMENT, params: { policyNumber: this.$route.params.policyNumber } });
    },

    preventInvalidCurrencyInput,
    formatDateToDisplay,
    reverseFormatFormDate,
  },
};
</script>

<style lang="scss" scoped>
.calculator-wrapper {
  --GAP-SIZE: #{spacer(3)};

  .calculator {
    max-width: spacer(53);
    margin: auto;

    .radio-group {
      &.large {
        &.button-wrapper {
          grid-template-columns: 1fr 1fr 1fr;
          input[type='reset'] {
            position: relative;

            &::before {
              content: 'Reset';
              position: absolute;
              left: 0; // adjust as needed
              top: -20px; // adjust as needed
              background-color: #f0f0f0; // optional: add background color
              padding: 5px; // optional: adjust padding
              border-radius: 3px; // optional: adjust border radius
            }
          }
        }
      }
    }

    .h3-DPT {
      margin-bottom: var(--HEADING-MARGIN);
      font-size: 24px;
    }

    .h3-no-DPT,
    .p,
    .radio-group:not(.button-wrapper),
    .result {
      margin-bottom: var(--GAP-SIZE);
    }

    :deep(.input-field) {
      margin-top: spacer(1);
      margin-bottom: 0.25rem;
    }

    --INPUT-PADDING: 10px 17px;
    .mock-input {
      display: flex;
      align-items: center;
      background: #fff;
      padding: var(--INPUT-PADDING);
      border: 1px solid _600;
      box-shadow: $FORM_ELEMENT_BOX_SHADOW;
      transition-property: background, border-color, box-shadow;

      .prefix {
        margin-right: 0.3em;

        & ~ #premium-calculate-input {
          border: none;
          box-shadow: none;
          padding: 0;
        }
      }

      &:hover {
        box-shadow: $FORM_ELEMENT_BOX_SHADOW, $FORM_ELEMENT_BOX_SHADOW_HOVER;
      }

      &.mock-input-focus {
        border-color: $LIGHT_GREEN;
      }
    }

    :deep(#premium-calculate-input) {
      display: block;
      background-color: $WHITE;
      color: $BODY_TEXT;
      padding: var(--INPUT-PADDING);
      border: none;

      &.payment-amount {
        padding-left: 40px;
      }

      &:read-only {
        border: unset !important;
        padding-left: 0 !important;
        background-color: unset !important;
        pointer-events: none;
      }

      &:focus {
        margin-bottom: unset !important;
      }

      @include mobile {
        font-size: font-size(1);
      }
      &.date-input {
        border: 1px solid $GREY_300;
        // Local fallback: ensure the calendar icon is visible even if other
        // input styles accidentally reset background images.
        background-image: url('/assets/public/images/calendar-icon.svg') !important;
        background-repeat: no-repeat !important;
        background-position: calc(100% - 16px) center !important;
        background-size: 16px 16px !important;
        padding-right: 48px;
      }

      // Ensure the shared DHDS calendar icon shows for the date-input variant.
      &.date-input[data-dhds-calendar-icon='true'] {
        background-image: url('/assets/public/images/calendar-icon.svg') !important;
        background-repeat: no-repeat !important;
        background-position: calc(100% - 16px) center !important;
        background-size: 16px 16px !important;
        padding-right: 48px;
      }
    }

    .validation-error-msg {
      color: $ERROR;
      display: block;
      font-size: font-size(-1);
    }

    .inline-message {
      margin-top: spacer(1);
    }

    .result {
      margin-top: var(--GAP-SIZE);
      margin-bottom: 8px;
      background: unset;
      display: flex;
      flex-direction: column;
      font-weight: $BOLD;
      gap: 10px;

      span {
        font-weight: normal;
      }

      p {
        margin-bottom: 0;
      }
    }

    .loading-premium {
      min-height: 200px;
    }

    .button-wrapper {
      margin-top: spacer(4);
    }

    button,
    input[type='submit'],
    input[type='reset'] {
      max-width: 100%;

      &#trigger-calculate {
        grid-column: 1;
      }
    }
  }
}

.calculator-and-info-container {
  margin-top: 56px;
  margin-bottom: 56px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;

  @include tablet-and-below {
    grid-template-columns: 1fr;
  }
}
.prefixed-style {
  border: none !important;
  box-shadow: none !important;
  border-bottom-left-radius: 0 !important;
  border-top-left-radius: 0 !important;
  padding: 10px !important;
}

.prefixed-input {
  @include flex-row;
  @include flex-v-center;
  background-color: $PRIMARY_GREY_20;
  line-height: 3;

  label {
    width: auto;
    height: 100%;
    margin-right: 12px;
    margin-left: 12px;
    text-align: center;
  }

  input {
    margin-bottom: 0;
  }
}

.wrapper-with-prefix {
  margin-top: 8px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  border: 1.5px solid $PRIMARY_GREY_40;
  background-color: $PRIMARY_GREY_20;
}

label {
  &.date-amount-input-label {
    font-weight: 420;
    pointer-events: none;
  }

  &.calculate-premium-by-header {
    display: inline-block;
    font-size: 16px;
    font-weight: 420;
    margin-bottom: 12px;
  }
}
</style>
